import React, { useEffect, useState, useCallback, useMemo } from "react";
import { ArrowLeft, Home, Building2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { createQuartersService } from "../../../services/quartersService";
import { useToast } from "../../../common_components/Toast";
import type { IFlat, IBuilding, IFloor } from "../types/quarters.types";

interface FlatDetailResponse {
  flat: IFlat;
  history: unknown[];
}

const FlatDetail = () => {
  const navigate = useNavigate();
  const { flatId } = useParams();
  const { apiRequest } = useAuth();
  const quartersService = useMemo(() => createQuartersService({ apiRequest }), [apiRequest]);
  const toast = useToast();

  const [flatData, setFlatData] = useState<FlatDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const loadFlat = useCallback(async () => {
    if (!flatId) return;
    setLoading(true);
    try {
      const res = await quartersService.getFlat(flatId);
      if (res.success) {
        setFlatData(res.data);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to load flat details");
      navigate("/quarters/inventory");
    } finally {
      setLoading(false);
    }
  }, [flatId, navigate, quartersService, toast]);

  useEffect(() => {
    loadFlat();
  }, [loadFlat]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  const flat = flatData?.flat;
  // Convenience cast: buildingId is always populated by the backend for this endpoint
  const building = flat && typeof flat.buildingId === "object" ? flat.buildingId as IBuilding : null;
  const floor = flat && typeof flat.floorId === "object" ? flat.floorId as IFloor : null;

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-10">
      <button
        onClick={() => navigate("/quarters/inventory")}
        className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-bold mb-6 group transition-colors"
      >
        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
        Back to Inventory
      </button>

      <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm max-w-3xl">
        <div className="flex items-start gap-4 mb-6">
          <div className="bg-blue-100 text-blue-700 p-4 rounded-2xl">
            <Home className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 font-serif">
              Flat {flat?.flatNumber}
            </h1>
            <p className="text-slate-500 text-sm mt-1 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {building?.name} ({building?.code})
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-[10px] text-slate-400 font-bold uppercase">Status</p>
            <p className="text-slate-800 font-bold mt-1 capitalize">{flat?.status}</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-[10px] text-slate-400 font-bold uppercase">Quarter Type</p>
            <p className="text-slate-800 font-bold mt-1">{building?.quarterType ?? "—"}</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-[10px] text-slate-400 font-bold uppercase">Floor</p>
            <p className="text-slate-800 font-bold mt-1">{floor?.name ?? floor?.floorNumber ?? "—"}</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-[10px] text-slate-400 font-bold uppercase">Area</p>
            <p className="text-slate-800 font-bold mt-1">{flat?.area ? `${flat.area} sq.ft` : "N/A"}</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-[10px] text-slate-400 font-bold uppercase">Bedrooms</p>
            <p className="text-slate-800 font-bold mt-1">{flat?.bedrooms ?? "N/A"}</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-[10px] text-slate-400 font-bold uppercase">Furnished</p>
            <p className="text-slate-800 font-bold mt-1 capitalize">{flat?.furnished?.replace("-", " ") || "N/A"}</p>
          </div>
        </div>

        {building?.address && (
          <div className="mt-6 p-4 bg-blue-50/50 border border-blue-100 rounded-xl">
            <p className="text-[10px] text-blue-600 font-bold uppercase">Campus Address</p>
            <p className="text-slate-700 text-sm mt-1">{building.address}</p>
            <p className="text-slate-500 text-xs mt-1">Campus: {building.campus || "Main Campus"}</p>
          </div>
        )}

        {building?.quarterType && (
          <div className="mt-6 border-t border-slate-100 pt-6 flex justify-end">
            <button
              onClick={() => navigate("/quarters/apply", { state: { quarterType: building.quarterType } })}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-6 py-2.5 rounded-xl shadow-md shadow-blue-200 transition flex items-center gap-2"
            >
              <Home className="w-4 h-4" /> Apply for {building.quarterType} Quarters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlatDetail;
